<script lang="ts">
  import CartStore from "@retailer/svelte/stores/CartStore";
  import { soldInUnits, soldInCases, isKeg } from "@shared/svelte/ProductHelpers.js";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import { discountUnitPrice, discountCasePrice } from "@retailer/svelte/ProductDetails/Helpers";
  import * as _ from "lodash";
  import { canCalculateOunces, pricePerOunce } from "@retailer/services/unit_calculation_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import type { CartProduct, Product } from "@shared/types/ProductLine";
  import type { CartType } from "@shared/types/Cart";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";

  export let product: Product;
  export let productLine;
  export let showPriceNotAvailable: boolean = false;
  export let showPricePerOunce: boolean = true;
  export let flexVertical: boolean = false;

  $: inventoryItemSoldBy = $InventoryItemStore?.inventoryItems[product.inventory_id]?.sold_by;
  $: castCartProduct = (<unknown>{ product_line: productLine, ...product }) as CartProduct;
  const displayPricePerOunce = stagedRolloutCheck("display_price_per_ounce") && showPricePerOunce;

  const getCartProduct = (cart: CartType, inventoryId: number): CartProduct => {
    const allProducts = (_.get(cart, "orders") || []).map(o => _.get(o, "products") || []).flat();
    return allProducts.find(p => p.inventory_id === inventoryId);
  };

  const getUnitLabel = () => {
    if (product.full_sleeve_only) {
      return product.container_carton_size
        ? `/${product.container_carton_size} pack of ${product.container_type}s`
        : "/pack";
    } else {
      return `/${product.container_type}`;
    }
  };

  let cartProduct: CartProduct;
  $: {
    cartProduct = getCartProduct($CartStore, product.inventory_id);
    if (cartProduct) {
      castCartProduct = {
        ...castCartProduct,
        unit_price_at_purchase: cartProduct.unit_price_at_purchase,
        case_price_at_purchase: cartProduct.case_price_at_purchase,
      };
    } else {
      castCartProduct = {
        ...castCartProduct,
        unit_price_at_purchase: null,
        case_price_at_purchase: null,
      };
    }
  }

  let discountedUnitPrice;
  $: {
    discountedUnitPrice = discountUnitPrice(castCartProduct);
  }
  let discountedCasePrice;
  $: {
    discountedCasePrice = discountCasePrice(castCartProduct);
  }
  $: isUnitDiscounted = castCartProduct.unit_price?.toFixed(2) !== discountedUnitPrice?.toFixed(2);
  $: isCaseDiscounted = castCartProduct.case_price?.toFixed(2) !== discountedCasePrice?.toFixed(2);
  let caseLabel = "/case";
  $: unitLabel = getUnitLabel();
  $: product_unit_price = product.unit_price;
  $: product_case_price = product.case_price;
</script>

<style>
  .price-string span {
    margin-left: 0;
  }
</style>

{#if product_case_price || product_unit_price}
  <span class="fizz-styles">
    <span class="price-string fizz-text-deal">
      {#if (soldInUnits(castCartProduct, productLine, inventoryItemSoldBy) || isKeg(castCartProduct)) && product_unit_price}
        <span class="unit-price">
          {#if isUnitDiscounted}
            <div class:fizz-flex-vertical={flexVertical} class:fizz-flex-row-4={!flexVertical}>
              <p class="color-black strikethrough" data-testid="strikethrough-unit-price">
                <FormatMoney input={product_unit_price} />
              </p>
              <p class="discount-price fizz-text-deal" data-testid="discount-unit-price">
                <FormatMoney input={discountedUnitPrice} />{unitLabel}
              </p>

              {#if canCalculateOunces(product) && displayPricePerOunce}
                <p class="fizz-text-regular">
                  (${pricePerOunce(product, discountedUnitPrice)}/oz)
                </p>
              {/if}
            </div>
          {:else}
            <span data-testid="unit-price">
              <FormatMoney input={product_unit_price} />{unitLabel}
            </span>
            {#if canCalculateOunces(product) && displayPricePerOunce}
              <span class="fizz-text-regular">
                (${pricePerOunce(product, product_unit_price)}/oz)
              </span>
            {/if}
          {/if}
        </span>
      {/if}
      {#if soldInCases(product, productLine, inventoryItemSoldBy) && product_case_price}
        <span class="case-price">
          {#if isCaseDiscounted}
            <div class:fizz-flex-vertical={flexVertical} class:fizz-flex-row-4={!flexVertical}>
              <p class="price color-black strikethrough" data-testid="strikethrough-case-price">
                <FormatMoney input={product_case_price} />
              </p>
              <p class="discount-price fizz-text-deal" data-testid="discount-case-price">
                <FormatMoney input={discountedCasePrice} />{caseLabel}
              </p>

              {#if canCalculateOunces(product, true) && displayPricePerOunce}
                <p class="fizz-text-regular">
                  (${pricePerOunce(product, discountedCasePrice, true)}/oz)
                </p>
              {/if}
            </div>
          {:else}
            <div class="price" data-testid="case-price">
              <FormatMoney input={product_case_price} />{caseLabel}
              {#if canCalculateOunces(product, true) && displayPricePerOunce}
                <span class="fizz-text-regular">
                  (${pricePerOunce(product, product_case_price, true)}/oz)
                </span>
              {/if}
            </div>
          {/if}
        </span>
      {/if}
    </span>
  </span>
{:else if showPriceNotAvailable}
  <div class="fizz-max-content" data-testid="price-not-available">Price not available</div>
{/if}
